/**
 * MyCopro - Auth Store
 * ✅ Support Admin (sans résidence), Syndic (multi-résidences), Impersonation
 * ✅ NOUVEAU: chatEnabled et requestsEnabled pour restriction d'accès
 */

import { create } from 'zustand';
import { User as FirebaseUser, signOut } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { doc, getDoc, setDoc, updateDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useResidenceStore } from './residenceStore';
import { useChatStore } from './chatStore';
import { logger } from '@/utils/logger';
import { splitMemberData, writeMemberPrivate } from '@/utils/memberPrivate';

export type UserCategory = 'tenant' | 'owner' | 'council' | 'syndic' | 'gardien' | 'admin';

export interface NotificationPreferences {
  announcements?: boolean;
  documents?: boolean;
  events?: boolean;
  requests?: boolean;
  polls?: boolean;
  chat?: boolean;
  messages?: boolean;
  emailDigest?: boolean;
}

export interface CurrentUser {
  id: string;
  uid: string;
  name: string;
  displayName?: string;
  email?: string;
  phone?: string;
  apartment?: string;
  identifier?: string;
  category: UserCategory;
  residenceId: string | null;        // ✅ Nullable pour admin/syndic
  residentId?: string;
  photoURL?: string;
  enabled?: boolean;
  status?: 'pending' | 'active' | 'rejected';
  landlordType?: 'private' | 'social' | '';
  landlordId?: string | null;
  notificationPrefs?: NotificationPreferences;
  // ✅ Champs Syndic
  syndicId?: string | null;
  syndicName?: string;
  syndicRole?: 'manager' | 'assistant' | 'readonly';
  // ✅ NOUVEAU: Restrictions d'accès fonctionnalités
  chatEnabled?: boolean;      // Accès à la messagerie (true par défaut)
  requestsEnabled?: boolean;  // Accès aux demandes (true par défaut)
  // Bâtiment du résident (null si résidence à bâtiment unique)
  building?: string | null;
  // Inscription refusée
  rejectionReason?: string | null;
  // Parrainage — code 6 chiffres affiché au candidat
  sponsorCode?: string | null;
  // ID de l'invitant (pour bloquer le parrainage par soi-même)
  invitedById?: string | null;
  // Mot de passe temporaire — force le changement à la première connexion
  mustChangePassword?: boolean;
  // Validation par un membre du CS (manuelle) — absent si auto-validation
  validatedAt?: string;
  validatedBy?: string;
}

// ✅ État d'impersonation
export interface ImpersonationState {
  isActive: boolean;
  originalUser: CurrentUser | null;
  targetUser: CurrentUser | null;
  startedAt: string | null;
}

interface AuthState {
  // Firebase user
  firebaseUser: FirebaseUser | null;
  setFirebaseUser: (user: FirebaseUser | null) => Promise<void>;

  // Current user data from Firestore
  currentUser: CurrentUser | null;
  setCurrentUser: (user: CurrentUser | null) => void;

  // ✅ Impersonation
  impersonation: ImpersonationState;
  startImpersonation: (targetUserId: string) => Promise<boolean>;
  stopImpersonation: () => Promise<void>;
  isImpersonating: () => boolean;
  getEffectiveUser: () => CurrentUser | null;

  // Auth state
  isAuthenticated: boolean;

  // Loading state
  isLoading: boolean;
  setLoading: (loading: boolean) => void;

  // Error
  error: string | null;

  // Actions
  loadUserData: (firebaseUser: FirebaseUser) => Promise<void>;
  updateProfile: (data: Partial<CurrentUser>) => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  firebaseUser: null,
  currentUser: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  // ✅ État d'impersonation initial
  impersonation: {
    isActive: false,
    originalUser: null,
    targetUser: null,
    startedAt: null
  },

  // Setter pour Firebase user
  setFirebaseUser: async (user) => {
    if (user) {
      set({ firebaseUser: user });
      await get().loadUserData(user);
    } else {
      useResidenceStore.getState().clearResidence();
      set({ 
        firebaseUser: null, 
        currentUser: null, 
        isAuthenticated: false,
        isLoading: false,
        impersonation: {
          isActive: false,
          originalUser: null,
          targetUser: null,
          startedAt: null
        }
      });
    }
  },

  setCurrentUser: (user) => set({ 
    currentUser: user,
    isAuthenticated: !!user
  }),
  
  setLoading: (loading) => set({ isLoading: loading }),

  // ✅ Vérifier si impersonation active
  isImpersonating: () => {
    return get().impersonation.isActive;
  },

  // ✅ Obtenir l'utilisateur effectif (impersonated si actif, sinon réel)
  getEffectiveUser: () => {
    const { impersonation, currentUser } = get();
    if (impersonation.isActive && impersonation.targetUser) {
      return impersonation.targetUser;
    }
    return currentUser;
  },

  // ✅ Démarrer l'impersonation (admin uniquement)
  startImpersonation: async (targetUserId: string) => {
    const { currentUser } = get();
    
    if (!currentUser || currentUser.category !== 'admin') {
      console.error('Impersonation: only admins can impersonate');
      return false;
    }

    try {
      // Charger l'utilisateur cible
      const [targetSnap, targetPrivateSnap] = await Promise.all([
        getDoc(doc(db, 'members', targetUserId)),
        getDoc(doc(db, 'memberPrivate', targetUserId)),
      ]);

      if (!targetSnap.exists()) {
        console.error('Impersonation: target user not found');
        return false;
      }

      const targetData = targetSnap.data();
      const targetPrivate = targetPrivateSnap.exists() ? targetPrivateSnap.data() : targetData;
      const targetUser: CurrentUser = {
        id: targetSnap.id,
        uid: targetUserId,
        name: targetData.name || 'Utilisateur',
        displayName: targetData.name,
        email: targetPrivate.email || '',
        phone: targetPrivate.phone || '',
        apartment: targetPrivate.apartment || '',
        identifier: targetPrivate.identifier || '',
        category: targetData.category || 'tenant',
        residenceId: targetData.residenceId || null,
        residentId: targetPrivate.residentId || '',
        photoURL: targetPrivate.photoURL || '',
        enabled: targetData.enabled !== false,
        status: targetData.status || 'active',
        landlordType: targetPrivate.landlordType || '',
        landlordId: targetPrivate.landlordId || null,
        notificationPrefs: targetPrivate.notificationPrefs || null,
        syndicId: targetData.syndicId || null,
        syndicRole: targetData.syndicRole || undefined,
        chatEnabled: targetPrivate.chatEnabled !== false,
        requestsEnabled: targetPrivate.requestsEnabled !== false,
        building: targetPrivate.building || null
      };

      // Logger l'impersonation pour audit
      await addDoc(collection(db, 'impersonation_logs'), {
        adminId: currentUser.id,
        adminName: currentUser.name,
        adminEmail: currentUser.email,
        targetId: targetUserId,
        targetName: targetUser.name,
        targetEmail: targetUser.email,
        targetCategory: targetUser.category,
        targetResidenceId: targetUser.residenceId,
        action: 'start',
        timestamp: Timestamp.now()
      });

      // Activer l'impersonation
      set({
        impersonation: {
          isActive: true,
          originalUser: currentUser,
          targetUser: targetUser,
          startedAt: new Date().toISOString()
        }
      });

      // Charger la résidence de l'utilisateur cible
      if (targetUser.residenceId) {
        await useResidenceStore.getState().loadResidence(targetUser.residenceId);
      } else {
        useResidenceStore.getState().clearResidence();
      }

      logger.auth(`Impersonation started: ${currentUser.name} → ${targetUser.name}`);
      return true;

    } catch (error) {
      console.error('[Impersonation] Error:', error);
      return false;
    }
  },

  // ✅ Arrêter l'impersonation
  stopImpersonation: async () => {
    const { impersonation } = get();

    if (!impersonation.isActive || !impersonation.originalUser) {
      return;
    }

    try {
      // Logger la fin d'impersonation
      await addDoc(collection(db, 'impersonation_logs'), {
        adminId: impersonation.originalUser.id,
        adminName: impersonation.originalUser.name,
        adminEmail: impersonation.originalUser.email,
        targetId: impersonation.targetUser?.id,
        targetName: impersonation.targetUser?.name,
        action: 'stop',
        duration: impersonation.startedAt 
          ? Math.round((Date.now() - new Date(impersonation.startedAt).getTime()) / 1000)
          : 0,
        timestamp: Timestamp.now()
      });

      logger.auth(`Impersonation stopped: returning to ${impersonation.originalUser.name}`);

      // Restaurer l'utilisateur original
      set({
        impersonation: {
          isActive: false,
          originalUser: null,
          targetUser: null,
          startedAt: null
        }
      });

      // Vider la résidence (admin n'a pas de résidence)
      useResidenceStore.getState().clearResidence();

    } catch (error) {
      console.error('[Impersonation] Error stopping:', error);
    }
  },

  // Charger les données utilisateur depuis Firestore
  loadUserData: async (firebaseUser: FirebaseUser) => {
    try {
      set({ isLoading: true, error: null });

      const memberRef = doc(db, 'members', firebaseUser.uid);
      const [memberSnap, memberPrivateSnap] = await Promise.all([
        getDoc(memberRef),
        getDoc(doc(db, 'memberPrivate', firebaseUser.uid)),
      ]);

      let currentUser: CurrentUser;

      // Détecter transition rejected → active pour confettis de bienvenue (parrainage)
      const previousStatus = get().currentUser?.status;
      if (memberSnap.exists()) {
        const newStatus = memberSnap.data().status;
        if (previousStatus === 'rejected' && newStatus === 'active') {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('showWelcomeConfetti', '1');
          }
        }
      }

      if (memberSnap.exists()) {
        const memberData = memberSnap.data();
        // Données privées : memberPrivate en priorité, fallback sur members (migration progressive)
        const p = memberPrivateSnap.exists() ? memberPrivateSnap.data() : memberData;
        currentUser = {
          id: memberSnap.id,
          uid: firebaseUser.uid,
          name: memberData.name || firebaseUser.displayName || 'Utilisateur',
          displayName: memberData.name || firebaseUser.displayName,
          email: p.email || firebaseUser.email || '',
          phone: p.phone || '',
          apartment: p.apartment || '',
          identifier: p.identifier || '',
          category: memberData.category || 'tenant',
          residenceId: memberData.residenceId || null,
          residentId: p.residentId || '',
          photoURL: p.photoURL || firebaseUser.photoURL || '',
          enabled: memberData.enabled !== false,
          status: memberData.status || 'active',
          landlordType: p.landlordType || '',
          landlordId: p.landlordId || null,
          notificationPrefs: p.notificationPrefs || null,
          syndicId: memberData.syndicId || null,
          syndicRole: memberData.syndicRole || undefined,
          chatEnabled: p.chatEnabled !== false,
          requestsEnabled: p.requestsEnabled !== false,
          building: p.building || null,
          rejectionReason: p.rejectionReason || null,
          sponsorCode: memberData.sponsorCode || null,
          invitedById: p.invitedById || null,
          mustChangePassword: memberData.mustChangePassword === true,
          validatedAt: memberData.validatedAt instanceof Timestamp
            ? memberData.validatedAt.toDate().toISOString()
            : memberData.validatedAt || undefined,
          validatedBy: memberData.validatedBy || undefined,
        };
        // Création lazy de memberPrivate si absent (migration progressive)
        if (!memberPrivateSnap.exists()) {
          writeMemberPrivate(firebaseUser.uid, {
            email: memberData.email || firebaseUser.email || '',
            phone: memberData.phone || '',
            apartment: memberData.apartment || '',
            building: memberData.building || null,
            identifier: memberData.identifier || '',
            residentId: memberData.residentId || '',
            photoURL: memberData.photoURL || '',
            landlordType: memberData.landlordType || '',
            landlordId: memberData.landlordId || null,
            notificationPrefs: memberData.notificationPrefs || null,
            chatEnabled: memberData.chatEnabled !== false,
            requestsEnabled: memberData.requestsEnabled !== false,
            rejectionReason: memberData.rejectionReason || null,
            invitedById: memberData.invitedById || null,
            joinedAt: memberData.joinedAt || null,
            createdAt: memberData.createdAt || null,
            updatedAt: memberData.updatedAt || null,
            residenceId: memberData.residenceId || null,
          }).catch(e => console.warn('[AuthStore] memberPrivate lazy create failed:', e));
        }
      } else {
        // Nouveau membre — les documents Firestore sont créés par RegisterPage/finalizeRegister
        // Ne pas créer ici : ça provoquerait une race condition où le setDoc de RegisterPage
        // deviendrait un UPDATE (document déjà existant) → rejeté par les règles isSelfProfileUpdate
        logger.auth('New user, profile will be created by registration flow');

        currentUser = {
          id: firebaseUser.uid,
          uid: firebaseUser.uid,
          name: firebaseUser.displayName || '',
          displayName: firebaseUser.displayName || undefined,
          email: firebaseUser.email || '',
          category: 'tenant' as UserCategory,
          residenceId: null,
          identifier: '',
          apartment: '',
          landlordType: '',
          landlordId: null,
          enabled: true,
          status: 'pending',
          chatEnabled: true,
          requestsEnabled: true
        };
      }

      set({
        currentUser,
        isAuthenticated: true,
        isLoading: false
      });

      // Charger la résidence si existante (pas pour admin/syndic)
      if (currentUser.residenceId) {
        logger.auth('Loading residence:', currentUser.residenceId);
        await useResidenceStore.getState().loadResidence(currentUser.residenceId);
      }

    } catch (error) {
      console.error('[AuthStore] Error loading user data:', error);
      set({ 
        isLoading: false,
        isAuthenticated: true,
        error: 'Erreur lors du chargement du profil'
      });
    }
  },

  // Mettre à jour le profil
  updateProfile: async (data: Partial<CurrentUser>) => {
    const { currentUser, impersonation } = get();
    
    // ✅ En mode impersonation, on ne peut pas modifier le profil
    if (impersonation.isActive) {
      logger.auth('Cannot update profile while impersonating');
      return;
    }

    if (!currentUser) return;

    try {
      const memberRef = doc(db, 'members', currentUser.id);
      const { publicData, privateData } = splitMemberData(data as Record<string, any>);

      const writes: Promise<void>[] = [];
      if (Object.keys(publicData).length > 0) {
        writes.push(updateDoc(memberRef, { ...publicData, updatedAt: new Date().toISOString() }));
      }
      if (Object.keys(privateData).length > 0) {
        writes.push(writeMemberPrivate(currentUser.id, { ...privateData, updatedAt: new Date().toISOString() }));
      }
      await Promise.all(writes);

      const updatedUser = { ...currentUser, ...data };
      set({ currentUser: updatedUser });

      if (data.residenceId && data.residenceId !== currentUser.residenceId) {
        await useResidenceStore.getState().loadResidence(data.residenceId);
      }
    } catch (error) {
      console.error('[AuthStore] Error updating profile:', error);
      throw error;
    }
  },

  // Déconnexion
  logout: () => {
    useChatStore.getState().resetStore();
    signOut(auth).catch(err => console.error('[AuthStore] signOut error:', err));
    useResidenceStore.getState().clearResidence();
    set({
      firebaseUser: null,
      currentUser: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      impersonation: {
        isActive: false,
        originalUser: null,
        targetUser: null,
        startedAt: null
      }
    });
  }
}));

export default useAuthStore;
